/**
 * Customer portal reminders: accepted requests, 15 min before, and at start (jobs + inquiries).
 */

const DISMISS_KEY = 'portal_customer_notif_dismissed';

export function getDismissedIds() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

export function dismissNotification(id) {
  try {
    const s = getDismissedIds();
    s.add(id);
    localStorage.setItem(DISMISS_KEY, JSON.stringify([...s]));
  } catch (_) {}
}

export function dismissNotificationIds(ids) {
  if (!ids || !ids.length) return;
  try {
    const s = getDismissedIds();
    for (const id of ids) s.add(id);
    localStorage.setItem(DISMISS_KEY, JSON.stringify([...s]));
  } catch (_) {}
}

export function combineDateTime(dateStr, timeStr) {
  if (!dateStr) return null;
  const t = timeStr && String(timeStr).trim() ? String(timeStr).trim() : '09:00';
  const norm = t.length <= 5 ? `${t}:00` : t;
  const d = new Date(`${dateStr}T${norm}`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function isTerminalJob(job) {
  const s = String(job?.status || '').toLowerCase();
  return s === 'completed' || s === 'cancelled';
}

function formatWhen(d) {
  if (!d) return '';
  const datePart = d.toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
  const timePart = d.toLocaleTimeString('en-AU', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  return `${datePart} · ${timePart}`;
}

/**
 * @param {{ jobs: any[], inquiries: any[], now: Date, dismissed: Set<string> }}
 * @returns {{ id: string, title: string, detail: string, sortKey: number }[]}
 */
export function buildPortalCustomerNotifications({ jobs, inquiries, now, dismissed }) {
  const out = [];
  const t = now.getTime();

  const push = (id, title, detail, sortKey) => {
    if (dismissed.has(id)) return;
    out.push({ id, title, detail, sortKey });
  };

  // Accepted portal inquiry → status new (owner confirmed)
  for (const inq of inquiries || []) {
    const st = String(inq.status || '').toLowerCase();
    if (st !== 'new') continue;
    const when = combineDateTime(inq.preferred_date || inq.scheduled_date, inq.preferred_time || inq.scheduled_time);
    const detail = when ? formatWhen(when) : 'Schedule confirmed';
    push(`accept-${inq.id}`, 'Service request accepted', detail, when ? when.getTime() : t);
  }

  const addScheduleReminders = (kind, labelNoun, idPrefix) => {
    const list = kind === 'job' ? jobs || [] : inquiries || [];
    for (const row of list) {
      if (kind === 'job' && isTerminalJob(row)) continue;

      const dateStr = kind === 'job' ? row.scheduled_date : row.preferred_date || row.scheduled_date;
      const timeStr = kind === 'job' ? row.scheduled_time : row.preferred_time || row.scheduled_time;
      const start = combineDateTime(dateStr, timeStr);
      if (!start) continue;

      const startMs = start.getTime();
      const idBase = `${idPrefix}-${row.id}-${dateStr}`;

      // 15 minutes before start (show during [T-15, T))
      const rem15End = startMs;
      const rem15Start = startMs - 15 * 60 * 1000;
      if (t >= rem15Start && t < rem15End) {
        push(
          `rem15-${idBase}`,
          `${labelNoun} starts in 15 minutes`,
          formatWhen(start),
          startMs - 1
        );
      }

      // At / just after start (show during [T, T+2h))
      const endWindow = startMs + 2 * 60 * 60 * 1000;
      if (t >= startMs && t < endWindow) {
        push(
          `start-${idBase}`,
          `${labelNoun} starting now`,
          formatWhen(start),
          startMs
        );
      }
    }
  };

  addScheduleReminders('job', 'Job', 'job');
  addScheduleReminders('inquiry', 'Visit', 'inq');

  out.sort((a, b) => b.sortKey - a.sortKey);
  return out;
}
